import Vue from 'vue';
import store from '@app/store';
import moment from 'moment';
import { CustomContentRenderContext } from '@fullcalendar/core';

export default class EventsService extends Vue {
  public events = {
    'order-item': {
      mounted: this.onMountedOrderItemEvent,
      destroy: this.onDestroyOrderItemEvent,
    },
    order: {
      mounted: this.onMountedOrderEvent,
      destroy: this.onDestroyOrderEvent,
    },
    subscription: {
      mounted: this.onMountedSubscriptionEvent,
      destroy: this.onDestroySubscriptionEvent,
    },
    'activity-session': {
      mounted: this.onMountedActivityGroupEvent,
      destroy: this.onDestroyActivityGroupEvent,
    },
  };

  onMountedOrderItemEvent(context: any): void {
    context.$root.$on('updateTotalTax', $event => {
      if (context.$refs.stdForm?.formModel) {
        const totalPrice = context.$refs.stdForm.formModel.total_price;
        const taxRate = context.$refs.stdForm.formModel.tax_rate
          ? context.$refs.stdForm.formModel.tax_rate / 100
          : 0;
        const totalTax = totalPrice && taxRate ? totalPrice - totalPrice / (1 + taxRate) : 0;
        context.$refs.stdForm.formModel.total_tax = totalTax;
      }
    });
    context.$root.$on('updateTotalPrice', $event => {
      if (context.$refs.stdForm?.formModel) {
        const discount = context.$refs.stdForm.formModel.discount
          ? context.$refs.stdForm.formModel.discount
          : 0;
        const discountType = context.$refs.stdForm.formModel.discount_type || 'percentage';
        const unitPrice = context.$refs.stdForm.formModel.price;
        const quantity = context.$refs.stdForm.formModel.quantity;

        let totalPrice = 0;
        if (discountType === 'fixed') {
          totalPrice = (unitPrice - discount) * quantity;
        } else {
          totalPrice = unitPrice * quantity * (1 - discount / 100);
        }
        context.$refs.stdForm.formModel.total_price = totalPrice + Number.EPSILON;
        context.$root.$emit('updateTotalTax');
      }
    });
    context.$root.$on('updateFormWithArticle', $event => {
      if ($event) {
        const taxRate = $event.tax ? $event.tax.rate : null;
        const accountNumber = $event.account ? $event.account.number : null;
        const analyticCode = $event.analytic_account ? $event.analytic_account.code : null;
        const price = $event.base_price ? $event.base_price : 0;
        const name = $event.name ? $event.name : null;
        context.$refs.stdForm.formModel.tax_rate = taxRate;
        context.$refs.stdForm.formModel.account_number = accountNumber;
        context.$refs.stdForm.formModel.analytic_code = analyticCode;
        context.$refs.stdForm.formModel.price = price;
        context.$refs.stdForm.formModel.article_name = name;
        context.$root.$emit('updateTotalPrice');
      }
    });

    context.$root.$on('updateFormWithArticlePrice', $event => {
      if ($event) {
        context.$refs.stdForm.formModel.price = $event.price;
      }
      context.$root.$emit('updateTotalPrice');
    });

    context.$root.$on('updateOrderItemFormWithAutomaticDiscount', $event => {
      if ($event) {
        const amount = $event.amount;
        const name = $event.name ? $event.name : null;
        if ($event.type === 'money') {
          context.$refs.stdForm.formModel.price = amount;
          context.$refs.stdForm.formModel.discount = null;
        } else if ($event.type === 'percent') {
          context.$refs.stdForm.formModel.discount = amount;
        }
        context.$refs.stdForm.formModel.article_name = name;
        context.$root.$emit('updateTotalPrice');
      }
    });
  }
  onDestroyOrderItemEvent(context: any): void {
    context.$root.$off('updateTotalTax');
    context.$root.$off('updateTotalPrice');
    context.$root.$off('updateFormWithArticle');
    context.$root.$off('updateFormWithArticlePrice');
    context.$root.$off('updateOrderItemFormWithAutomaticDiscount');
  }

  onMountedOrderEvent(context: any): void {
    context.$root.$on('calculateTotalPrice', $event => {
      const form = context.$refs.stdForm ? 'stdForm' : 'drawerStdForm';
      const orderItems = context.$refs[form].formModel.orderItems;
      let totalPrice: number = 0;
      let totalTax: number = 0;
      orderItems.forEach(item => {
        const discount = item.discount ? item.discount : 0;
        const discount_type = item.discount_type ? item.discount_type : 'percentage';
        let itemTotalPrice = 0;
        if (discount_type === 'percentage') {
          itemTotalPrice = item.price * item.quantity * (1 - discount / 100);
        } else {
          itemTotalPrice = item.price * item.quantity - discount;
        }
        totalPrice += itemTotalPrice;
        totalTax += itemTotalPrice - itemTotalPrice / (1 + parseFloat(item.tax_rate) / 100);
      });
      context.$set(
        context.$refs[form].formModel,
        'total_price',
        Number.isNaN(totalPrice) ? null : totalPrice.toFixed(2),
      );
      context.$set(
        context.$refs[form].formModel,
        'total_tax',
        Number.isNaN(totalTax) ? null : totalTax.toFixed(2),
      );
    });
  }
  onDestroyOrderEvent(context: any): void {
    context.$root.$off('calculateTotalPrice');
  }

  onMountedSubscriptionEvent(context: any): void {
    context.$root.$on('setCategoryFeaturesForContactForm', event => {
      // Set subscription dates
      if (context.modelLoaded && context.modelLoaded.subscription_category_id === event.id) {
        context.$set(
          context.$refs.stdForm?.formModel,
          'start_date',
          context.modelLoaded.start_date,
        );
        context.$set(context.$refs.stdForm?.formModel, 'end_date', context.modelLoaded.end_date);
      } else {
        let startDate = null;
        if (event['start_date_type_value'] === 'fixed_date') {
          startDate = event['fixed_start_date'];
        } else {
          startDate = moment().format('YYYY-MM-DD');
        }

        context.$set(context.$refs.stdForm?.formModel, 'start_date', startDate);

        let endDate: string | null;
        switch (event['end_date_type_value']) {
          case 'fixed_date':
            endDate = event['fixed_end_date'];
            break;
          case 'fixed_duration':
            endDate = moment(startDate)
              .add(event['fixed_duration_number'], event['duration_type_value'])
              .format('YYYY-MM-DD');
            break;
          case 'without_end_date':
          default:
            endDate = null;
            break;
        }

        context.$set(context.$refs.stdForm?.formModel, 'end_date', endDate);
      }

      // Set subscription credits
      if (context.modelLoaded && context.modelLoaded.subscription_category_id === event.id) {
        context.$set(
          context.$refs.stdForm?.formModel,
          'total_credits',
          context.modelLoaded['total_credits'],
        );
        context.$set(
          context.$refs.stdForm?.formModel,
          'remaining_credits',
          context.modelLoaded['remaining_credits'],
        );
      } else {
        context.$set(context.$refs.stdForm?.formModel, 'total_credits', event['number_credits']);
        context.$set(
          context.$refs.stdForm?.formModel,
          'remaining_credits',
          event['number_credits'],
        );
      }
    });
    context.$root.$on('updateSubscriptionPriceForContactForm', event => {
      if (context.modelLoaded && context.modelLoaded.subscription_category_id === event.id) {
        context.$set(
          context.$refs.stdForm?.formModel,
          'subscription_price',
          context.modelLoaded.subscription_price,
        );
      } else {
        const article = context.$refs.stdForm?.formModel?.article;
        if (article) {
          const basePath = context.$bpcApiRouteHelper.getEntityPath('subscriptions');
          context.$bpcApi
            .post(basePath + '/subscription-price', {
              article: article,
              subscription: context.$refs.stdForm?.formModel,
            })
            .subscribe(
              res => {
                context.$set(
                  context.$refs.stdForm?.formModel,
                  'subscription_price',
                  Number(res.data).toFixed(2),
                );
              },
              error => {
                console.log(error);
              },
            );
        }
      }
    });
  }
  onDestroySubscriptionEvent(context: any): void {
    context.$root.$off('setCategoryFeaturesForContactForm');
    context.$root.$off('updateSubscriptionPriceForContactForm');
  }

  onMountedActivityGroupEvent(context: any): void {
    context.$root.$on('generateSessionsFinished', event => {
      // refresh table
      context.$refs.provider.getEntities();
    });
    context.$root.$on('setActivityGroupIdFromClub', event => {
      console.log('FUNCIONA', event['club_activity_group_id']);
      context.$refs.stdForm.formModel.activity_group_id = event['club_activity_group_id'];
      context.$refs.stdForm.formModel.activityGroup = event['club_activity_group_id'];
    });
  }

  onDestroyActivityGroupEvent(context: any): void {
    context.$root.$off('generateSessionsFinished');
    context.$root.$off('setActivityGroupIdFromClub');
  }
}
